// src/payment/components/PaymentModal.js
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { 
  LuX, 
  LuShieldCheck, 
  LuQrCode, 
  LuCreditCard, 
  LuLock, 
  LuSparkles, 
  LuCircleAlert,
  LuRefreshCw
} from 'react-icons/lu';
import { PaymentService } from '../core/PaymentService';
import { usePaymentHandler } from '../hooks/usePaymentHandler';
import axios from 'axios';
import ENV from '../../api/config/env.config';
import api from '../../api/axios';
import PaymentSummary from './PaymentSummary';
import QRPayment from './QRPayment';
import StripePayment from './StripePayment';
import StatusModal from './StatusModal';
import './PaymentModal.css';

const PaymentModal = ({ 
  resumen, 
  onClose, 
  onSubmit, 
  onTabChange,
  slug: propSlug 
}) => {
  const slug = propSlug || resumen?.slug;
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState({
    qr: false,
    stripe: false,
    loading: true,
    error: null
  });
  const [showStatus, setShowStatus] = useState(false);
  const [statusData, setStatusData] = useState({ 
    status: 'success', 
    message: '',
    errorCode: null 
  });
  const [resumenWithFee, setResumenWithFee] = useState(resumen);

  const {
    loading,
    error,
    qrData,
    generateQR,
    startPolling,
    processStripePayment,
    reset,
    stopPolling,
  } = usePaymentHandler(slug, resumenWithFee, () => { // 🟢 FIX: antes usaba `resumen` (sin fee)
    setStatusData({
      status: 'success',
      message: '¡Pago completado exitosamente! Tu cupo está asegurado.'
    });
    setShowStatus(true);
  });

  useEffect(() => {
    const loadMethods = async () => {
      try {
        const methods = await PaymentService.getAvailablePaymentMethods(slug);
        setPaymentMethods({
          qr: methods.qr,
          stripe: methods.stripe,
          loading: false,
          error: null
        });
        
        if (methods.qr && !methods.stripe) {
          setSelectedMethod('qr');
        } else if (!methods.qr && methods.stripe) {
          setSelectedMethod('stripe');
        }
      } catch (err) {
        console.error('[PaymentModal] Error cargando métodos:', err);
        setPaymentMethods({
          qr: false,
          stripe: false,
          loading: false,
          error: err.message || 'Error al conectar con la pasarela.'
        });
      }
    };
    loadMethods();

    return () => {
      stopPolling();
      reset();
    };
  }, [slug, reset, stopPolling]);

  // PaymentModal.js - Agregar este useEffect
useEffect(() => {
  const fetchFee = async () => {
    if (!resumen || !selectedMethod) return;
    
    try {
      // 🔧 FIX: antes usaba fetch(`${process.env.REACT_APP_BASE_URL}/fee-config/${slug}`),
      // que (a) apuntaba a una URL incompleta (le faltaba /admin, y
      // REACT_APP_BASE_URL ni siquiera es la variable que usa el resto del
      // proyecto) y (b) aunque la URL estuviera bien, /admin/fee-config
      // requiere sesión de Superadministrador — un competidor pagando nunca
      // tiene esa sesión, así que siempre iba a fallar. Ahora usa el
      // endpoint público de solo lectura (sin auth), igual patrón que
      // /public/payment-methods.
      const response = await api.get(
        `/public/fee-config/${slug}`
    );
      const feeConfig = response.data;
      const config = feeConfig[selectedMethod];
        
        let feeAplicado = 0;
        const montoBase = parseFloat(resumen.monto) || 0; // precio puro del paquete, tal como lo valida el backend
        let montoConFee = montoBase;
        // 🟢 FIX (mezcla de monedas): montoBase siempre está en Bs. (así se
        // genera el QR y así se define el precio del paquete). El fee de QR
        // también se configura en Bs. en el admin, así que se suma directo.
        // Pero el fee FIJO de Stripe se configura en $us (ver
        // FeaturedEventsAdmin.js, label "Monto ($us):"), así que antes de
        // sumarlo a montoBase hay que convertirlo a Bs. con la misma tasa de
        // cambio del paquete. Sin esto, `100 Bs + 1 (pensado como $us)` se
        // sumaban como si fueran la misma unidad, dando un total incorrecto.
        const conversionRate = parseFloat(resumen?.paqueteSeleccionado?.conversion_rate) || 6.96;
        
        if (config?.is_enabled && config?.fee_value > 0) {
          if (config.fee_type === 'percentage') {
            feeAplicado = (montoBase * config.fee_value) / 100;
          } else if (selectedMethod === 'stripe') {
            feeAplicado = config.fee_value * conversionRate; // USD -> Bs.
          } else {
            feeAplicado = config.fee_value; // QR: ya está en Bs.
          }
          
          montoConFee = montoBase + feeAplicado;
        }
        
        // 🟢 FIX (corregido tras revisar el backend): `monto` NO se debe
        // sobreescribir. adminStripeConfigController.createPaymentIntent y
        // bnbController.generateQR validan que `datos_inscripcion_guardado.monto`
        // (que sale de este mismo `resumen.monto`) coincida con el precio
        // PURO del paquete calculado desde la BD (sin fee) — si aquí
        // metiéramos el total con fee, esa validación fallaría y el backend
        // rechazaría el pago con "El monto no corresponde al paquete...".
        // El fee de plataforma lo calcula el backend de forma 100%
        // independiente (ver feeService.js). `montoConFee` queda como campo
        // aparte, solo para mostrarlo en pantalla y para que
        // PaymentService.registerFormData registre el total real cobrado.
        setResumenWithFee({
          ...resumen,
          feeAplicado,
          montoConFee,
          feeConfig
        });
    } catch (error) {
      console.error('[PaymentModal] Error al obtener fee:', error);
    }
  };
  
  fetchFee();
}, [resumen, selectedMethod, slug]);

  const handleQRPayment = useCallback(async () => {
    try {
      reset();
      const data = await generateQR((progress) => {
        console.log(`[PaymentModal] Progreso QR: ${Math.round(progress)}%`);
      });
      
      await startPolling(data.qrId, () => {
        setStatusData({
          status: 'success',
          message: '¡Pago QR completado exitosamente! Nos vemos en la carrera.'
        });
        setShowStatus(true);
      });
    } catch (err) {
      console.error('[PaymentModal] Error en pago QR:', err);
      setStatusData({
        status: 'error',
        message: `No pudimos procesar tu QR: ${err.message}`,
        errorCode: err.code || 'QR_ERROR'
      });
      setShowStatus(true);
    }
  }, [generateQR, startPolling, reset, onSubmit]);

  const handleStripePayment = useCallback(async (cardElement, stripeInstance) => {
    try {
      reset();
      await processStripePayment(cardElement, () => {
        setStatusData({
          status: 'success',
          message: '¡Pago con tarjeta verificado con éxito! Registro completado.'
        });
        setShowStatus(true);
      }, stripeInstance);
    } catch (err) {
      console.error('[PaymentModal] Error en pago Stripe:', err);
      setStatusData({
        status: 'error',
        message: `Error con la tarjeta: ${err.message}`,
        errorCode: err.code || 'STRIPE_ERROR'
      });
      setShowStatus(true);
    }
  }, [processStripePayment, reset, onSubmit]);

  const handleStatusClose = useCallback(() => {
    setShowStatus(false);
    if (statusData.status === 'success') {
      if (onTabChange) {
        onTabChange('competitors');
      }
      onSubmit?.();
      onClose();
    } else {
      reset();
      setSelectedMethod(null);
    }
  }, [statusData.status, onTabChange, onSubmit, onClose, reset]);

  if (!resumen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  };

  // 1. CARGANDO MÉTODOS
  if (paymentMethods.loading) {
    return (
      <div className="pay-modal-overlay">
        <div className="pay-modal-card pay-modal-state-card">
          <LuRefreshCw className="pay-modal-spinner-icon" />
          <h3 className="pay-modal-state-title">Asegurando conexión segura...</h3>
          <p className="pay-modal-state-sub">Consultando métodos de pago disponibles</p>
        </div>
      </div>
    );
  }

  // 2. ERROR DE CARGA
  if (paymentMethods.error) {
    return (
      <div className="pay-modal-overlay">
        <div className="pay-modal-card pay-modal-state-card">
          <div className="pay-modal-error-badge">
            <LuCircleAlert />
          </div>
          <h3 className="pay-modal-state-title">Ocurrió un inconveniente</h3>
          <p className="pay-modal-state-sub">{paymentMethods.error}</p>
          <button className="pay-modal-retry-btn" onClick={() => window.location.reload()}>
            Reintentar conexión
          </button>
        </div>
      </div>
    );
  }

  // 3. SIN MÉTODOS DISPONIBLES
  if (!paymentMethods.qr && !paymentMethods.stripe) {
    return (
      <div className="pay-modal-overlay">
        <div className="pay-modal-card pay-modal-state-card">
          <LuCircleAlert className="pay-modal-empty-icon" />
          <h3 className="pay-modal-state-title">Inscripciones en pausa</h3>
          <p className="pay-modal-state-sub">No hay métodos de pago habilitados en este momento para este evento.</p>
          <button className="pay-modal-close-action-btn" onClick={onClose}>
            Entendido
          </button>
        </div>
      </div>
    );
  }

  // 4. PANTALLA DE SELECCIÓN DE MÉTODO (MENÚ PRINCIPAL)
  if (!selectedMethod) {
    return (
      <div className="pay-modal-overlay" onClick={handleOverlayClick}>
        <div className="pay-modal-card">
          {/* Botón Cerrar */}
          <button className="pay-modal-close-btn" onClick={onClose} type="button" aria-label="Cerrar">
            <LuX />
          </button>

          {/* Encabezado con toque Pro & Cercano */}
          <div className="pay-modal-header">
            <div className="pay-modal-badge">
              <LuShieldCheck className="badge-icon" /> Pago 100% Seguro
            </div>
            <h2 className="pay-modal-title">¡Estás a un paso de la meta!</h2>
            <p className="pay-modal-subtitle">Confirma tu inscripción de manera rápida y protegida</p>
          </div>

          {/* Resumen del Pago */}
          <div className="pay-modal-summary-box">
            {/* 🟢 En esta pantalla aún no hay método seleccionado, así que
                fetchFee todavía no corrió: resumenWithFee == resumen (sin
                fee), lo cual es correcto porque el fee depende del método
                que el usuario todavía no eligió. */}
            <PaymentSummary resumen={resumenWithFee} compact />
          </div>

          {/* Selector de Métodos de Pago */}
          <div className="pay-modal-selector">
            <h3 className="pay-modal-selector-title">
              <LuSparkles className="inline-sparkle" /> Selecciona tu método preferido
            </h3>
            
            <div className="pay-modal-options-list">
              {paymentMethods.qr && (
                <button 
                  className="pay-modal-option-btn qr-option" 
                  onClick={() => setSelectedMethod('qr')}
                  type="button"
                >
                  <div className="option-icon-wrapper">
                    <LuQrCode />
                  </div>
                  <div className="option-info">
                    <span className="option-name">Pago por QR Bancario</span>
                    <span className="option-desc">Simple Directo desde la App de tu banco</span>
                  </div>
                  <span className="option-tag glow-qr">Rápido</span>
                </button>
              )}

              {paymentMethods.stripe && (
                <button 
                  className="pay-modal-option-btn stripe-option" 
                  onClick={() => setSelectedMethod('stripe')}
                  type="button"
                >
                  <div className="option-icon-wrapper">
                    <LuCreditCard />
                  </div>
                  <div className="option-info">
                    <span className="option-name">Tarjeta de Débito / Crédito</span>
                    <span className="option-desc">Visa, Mastercard o procesadores internacionales</span>
                  </div>
                  <span className="option-tag glow-stripe">Internacional</span>
                </button>
              )}
            </div>
          </div>

          {/* Footer de Confianza & Pasarelas Aceptadas */}
          <div className="pay-modal-footer">
            <div className="pay-modal-security-note">
              <LuLock className="lock-icon" />
              <span>Transacción encriptada de extremo a extremo</span>
            </div>
            
            <div className="pay-modal-badges-container">
              <img src="/icons/qr.svg" alt="QR Simple" className="pay-method-logo" />
              <img src="/icons/visa.svg" alt="Visa" className="pay-method-logo" />
              <img src="/icons/mastercard.svg" alt="Mastercard" className="pay-method-logo" />
              <img src="/icons/stripe.svg" alt="Stripe" className="pay-method-logo" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showStatus) {
    return (
      <StatusModal
        status={statusData.status}
        message={statusData.message}
        errorCode={statusData.errorCode}
        onClose={handleStatusClose}
        redirectToCompetitors={() => onTabChange?.('competitors')}
      />
    );
  }

  // 5. PANTALLA QR
  if (selectedMethod === 'qr') {
    return (
      <QRPayment
        resumen={resumenWithFee} // 🟢 FIX: antes `resumen` (sin fee)
        qrData={qrData}
        loading={loading}
        error={error}
        onGenerate={handleQRPayment}
        onBack={() => {
          reset();
          setSelectedMethod(null);
        }}
        onClose={onClose}
      />
    );
  }

  // 6. PANTALLA STRIPE
  if (selectedMethod === 'stripe') {
    return (
      <StripePayment
        resumen={resumenWithFee} // 🟢 FIX: antes `resumen` (sin fee)
        loading={loading}
        error={error}
        onPay={handleStripePayment}
        onBack={() => {
          reset();
          setSelectedMethod(null);
        }}
        onClose={onClose}
      />
    );
  }

  return null;
};

PaymentModal.propTypes = {
  resumen: PropTypes.shape({
    slug: PropTypes.string,
    monto: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    paqueteSeleccionado: PropTypes.object,
    camposActivos: PropTypes.arrayOf(
      PropTypes.shape({
        nombre: PropTypes.string,
        valor: PropTypes.any,
      })
    ),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  onTabChange: PropTypes.func,
  slug: PropTypes.string,
};

export default PaymentModal;